// src/lib/utils/date.ts

import { format, isValid, parseISO, differenceInDays, formatDistanceToNow } from 'date-fns';

/**
 * Formats a date string into a human-readable format
 * @param dateString - ISO date string or Date object
 * @param formatString - Optional date-fns format string (defaults to 'PP')
 * @returns Formatted date string or 'Invalid date' if parsing fails
 */
export const formatDate = (
  dateString: string | Date | undefined | null,
  formatString: string = 'PP'
): string => {
  if (!dateString) return 'Unknown date';

  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;

    if (!isValid(date)) {
      console.warn('Invalid date:', dateString);
      return 'Invalid date';
    }

    return format(date, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Validates if a string is a valid ISO date
 * @param dateString - String to validate
 * @returns boolean indicating if string is valid ISO date
 */
export const isValidISODate = (dateString: string): boolean => {
  try {
    return isValid(parseISO(dateString));
  } catch {
    return false;
  }
};

/**
 * Returns a relative time string (e.g., "2 days ago")
 * @param dateString - ISO date string or Date object
 * @returns Relative time string
 */
export const getRelativeTimeString = (dateString: string | Date): string => {
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.error('Error getting relative time:', error);
    return 'Unknown time ago';
  }
};

/**
 * Formats a date range between two dates
 * @param startDate - Start date string or Date object
 * @param endDate - End date string or Date object
 * @param formatString - Optional date-fns format string
 * @returns Formatted date range string
 */
export const formatDateRange = (
  startDate: string | Date,
  endDate: string | Date,
  formatString: string = 'PP'
): string => {
  try {
    const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
    const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;

    if (!isValid(start) || !isValid(end)) {
      return 'Invalid date range';
    }

    return `${format(start, formatString)} - ${format(end, formatString)}`;
  } catch (error) {
    console.error('Error formatting date range:', error);
    return 'Invalid date range';
  }
};

/**
 * Gets the duration between two dates in days
 * @param startDate - Start date string or Date object
 * @param endDate - End date string or Date object
 * @returns Number of days between dates or null if invalid
 */
export const getDurationInDays = (
  startDate: string | Date,
  endDate: string | Date
): number | null => {
  try {
    const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
    const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;

    if (!isValid(start) || !isValid(end)) {
      return null;
    }

    return differenceInDays(end, start);
  } catch (error) {
    console.error('Error calculating duration:', error);
    return null;
  }
};

/**
 * Formats a date with fallback value if date is invalid
 * @param dateString - Date string to format
 * @param fallback - Fallback string to use if date is invalid
 * @param formatString - Optional date-fns format string
 * @returns Formatted date string or fallback value
 */
export const formatDateWithFallback = (
  dateString: string | undefined | null,
  fallback: string = 'Not available',
  formatString: string = 'PP'
): string => {
  if (!dateString) return fallback;

  try {
    const date = parseISO(dateString);
    return isValid(date) ? format(date, formatString) : fallback;
  } catch {
    return fallback;
  }
};

/**
 * Formats a timestamp with both date and time
 * @param dateString - Date string to format
 * @returns Formatted timestamp string
 */
export const formatTimestamp = (dateString: string | Date): string => {
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
    return format(date, 'PPpp');
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Invalid timestamp';
  }
};

/**
 * Checks if a date is older than specified number of days
 * @param dateString - Date string to check
 * @param days - Number of days
 * @returns boolean indicating if date is older than specified days
 */
export const isOlderThan = (dateString: string | Date, days: number): boolean => {
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
    const difference = differenceInDays(new Date(), date);
    return difference > days;
  } catch {
    return false;
  }
};

/**
 * Type for custom date formats
 */
export type DateFormat = 'short' | 'medium' | 'long' | 'full';

/**
 * Format mapping for different date format types
 */
export const DATE_FORMATS: Record<DateFormat, string> = {
  short: 'P',
  medium: 'PP',
  long: 'PPP',
  full: 'PPPP'
};

/**
 * Formats a date using predefined format types
 * @param dateString - Date string to format
 * @param formatType - Type of format to use
 * @returns Formatted date string
 */
export const formatDateByType = (
  dateString: string | Date,
  formatType: DateFormat = 'medium'
): string => {
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
    return format(date, DATE_FORMATS[formatType]);
  } catch (error) {
    console.error('Error formatting date by type:', error);
    return 'Invalid date';
  }
};

/**
 * Utility type for date validation results
 */
export interface DateValidationResult {
  isValid: boolean;
  errorMessage?: string;
  parsedDate?: Date;
}

/**
 * Validates and parses a date string with detailed results
 * @param dateString - Date string to validate
 * @returns DateValidationResult object
 */
export const validateDate = (dateString: string): DateValidationResult => {
  try {
    const parsedDate = parseISO(dateString);

    if (!isValid(parsedDate)) {
      return {
        isValid: false,
        errorMessage: 'Invalid date format'
      };
    }

    return {
      isValid: true,
      parsedDate
    };
  } catch (error) {
    return {
      isValid: false,
      errorMessage: error instanceof Error ? error.message : 'Unknown error parsing date'
    };
  }
};